import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useEffect } from 'react'
import { axiosClient } from '../clients/axios-client'
import { useAuth } from '../hooks/useAuth'

export function useAuthenticatedApi() {
  const { getAccessToken, isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('User is not authenticated, skipping API interceptor setup')
      return
    }

    const requestInterceptor = axiosClient.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await getAccessToken()
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        } else {
          console.log('No token available for API request')
        }
        return config
      },
      (error: AxiosError) => Promise.reject(error)
    )

    const responseInterceptor = axiosClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          try {
            const originalRequest = error.config as InternalAxiosRequestConfig & {
              _retry?: boolean
            }
            if (originalRequest._retry) {
              // This is the second 401 for the same request: Stop
              throw error
            }
            originalRequest._retry = true

            const newToken = await getAccessToken({ forceRefresh: true })
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return axiosClient(originalRequest)
            }

            throw error
          } catch {
            // Token refresh failed, let the error propagate
            // AuthProvider will handle setting isAuthenticated to false
          }
        }
        throw error
      }
    )

    return () => {
      axiosClient.interceptors.request.eject(requestInterceptor)
      axiosClient.interceptors.response.eject(responseInterceptor)
    }
  }, [isAuthenticated, getAccessToken])
}
